/* ============================================================
   COREVIAN UNIVERSITY
   EXECUTIVE COMMAND CENTER
   CEO-ONLY EXECUTIVE RESOURCE ENVIRONMENT

   SECURITY MODEL
   1. Uses the SAME Supabase client as Corevian University.
   2. Reads the authenticated Supabase session directly.
   3. Requires Raquia Compass's exact Auth UUID.
   4. Requires the CEO-only Bible record through Supabase RLS.
   5. Uses short-lived signed URLs for private Storage access.
   ============================================================ */

(function () {
  'use strict';

  const CVN_EXECUTIVE = {
    authorizedUserId: 'e61d0c96-b6d1-4c09-bead-51c0103044a2',
    bibleDocumentCode: 'CVN-BIBLE-001'
  };

  let executiveInstalled = false;
  let bibleRecord = null;

  /* ============================================================
     UNIVERSITY / AUTH HELPERS
     ============================================================ */

  function isDemoMode() {
    const banner = document.getElementById('demoBanner');

    if (!banner) return false;

    return window.getComputedStyle(banner).display !== 'none';
  }

function getUniversitySupabaseClient() {
  try {
    if (
      typeof window.getCorevianSupabaseClient !== 'function'
    ) {
      console.error(
        'Corevian University authenticated Supabase client is not available.'
      );
      return null;
    }

    return window.getCorevianSupabaseClient();

  } catch (error) {
    console.error(
      'Corevian Executive Command Center client error:',
      error
    );
    return null;
  }
}

  async function getAuthenticatedSession() {
    const client = getUniversitySupabaseClient();

    if (!client) {
      return null;
    }

    const { data, error } =
      await client.auth.getSession();

    if (error) {
      throw error;
    }

    return data?.session || null;
  }

  function isAuthorizedCEO(session) {
    return (
      session?.user?.id ===
      CVN_EXECUTIVE.authorizedUserId
    );
  }

  /* ============================================================
     HELPERS
     ============================================================ */

  function escapeHTML(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function formatDate(value) {
    if (!value) return '—';

    try {
      const date = new Date(`${value}T00:00:00`);

      if (Number.isNaN(date.getTime())) {
        return escapeHTML(value);
      }

      return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return escapeHTML(value);
    }
  }

  function setStatus(message, type = '') {
    const target =
      document.getElementById('cvnExecutiveStatus');

    if (!target) return;

    target.textContent = message;

    target.className =
      `cvn-executive-status ${
        type
          ? `cvn-executive-status-${type}`
          : ''
      }`;
  }

  /* ============================================================
     CEO-ONLY DATABASE ACCESS
     ============================================================ */

  async function getBibleRecord(client) {
    /*
      IMPORTANT:
      Supabase RLS is the backend security boundary.

      Even after the exact UUID check, the Bible record must
      still be returned by the protected ceo_bible_documents
      table before this interface can be installed.
    */

    const { data, error } = await client
      .from('ceo_bible_documents')
      .select(
        [
          'id',
          'document_code',
          'title',
          'version',
          'effective_date',
          'classification',
          'storage_bucket',
          'storage_path',
          'status',
          'owner_user_id'
        ].join(',')
      )
      .eq(
        'document_code',
        CVN_EXECUTIVE.bibleDocumentCode
      )
      .eq('status', 'active')
      .maybeSingle();

    if (error) {
      throw error;
    }

    if (!data) {
      return null;
    }

    if (
      data.owner_user_id !==
      CVN_EXECUTIVE.authorizedUserId
    ) {
      return null;
    }

    return data;
  }

  /* ============================================================
     PRIVATE STORAGE ACCESS
     ============================================================ */

  async function createSignedBibleUrl(
    client,
    download = false
  ) {
    if (!bibleRecord) {
      throw new Error(
        'The controlled Bible record is not available.'
      );
    }

    if (
      !bibleRecord.storage_bucket ||
      !bibleRecord.storage_path
    ) {
      throw new Error(
        'The controlled Bible storage location is incomplete.'
      );
    }

    /*
      Supabase Storage policy must independently authorize
      this signed-in user before a signed URL is issued.
    */

    const { data, error } = await client.storage
      .from(bibleRecord.storage_bucket)
      .createSignedUrl(
        bibleRecord.storage_path,
        60,
        download
          ? {
              download:
                'Corevian-Bible-v3.0-Controlled-Copy.docx'
            }
          : undefined
      );

    if (error) {
      throw error;
    }

    if (!data?.signedUrl) {
      throw new Error(
        'Supabase did not return a private document link.'
      );
    }

    return data.signedUrl;
  }

  async function openBible(download = false) {
    const button =
      document.getElementById(
        download
          ? 'cvnBibleDownload'
          : 'cvnBibleOpen'
      );

    const originalText =
      button?.textContent || '';

    try {
      if (button) {
        button.disabled = true;
        button.textContent = 'Authorizing...';
      }

      setStatus(
        'Verifying CEO authorization...'
      );

      /*
        Re-read the REAL authenticated session every time
        the controlled master is requested.
      */

      const session =
        await getAuthenticatedSession();

      if (
        !session ||
        !isAuthorizedCEO(session)
      ) {
        throw new Error(
          'CEO authorization is not available.'
        );
      }

      const client =
        getUniversitySupabaseClient();

      if (!client) {
        throw new Error(
          'Secure University connection is not available.'
        );
      }

      /*
        Recheck CEO-only RLS before every document access.
      */

      bibleRecord =
        await getBibleRecord(client);

      if (!bibleRecord) {
        throw new Error(
          'CEO authorization for this controlled resource was not confirmed.'
        );
      }

      const url =
        await createSignedBibleUrl(
          client,
          download
        );

      setStatus(
        download
          ? 'Controlled copy authorized.'
          : 'Secure Bible access authorized.',
        'success'
      );

      window.open(
        url,
        '_blank',
        'noopener,noreferrer'
      );

    } catch (error) {
      console.error(
        'Corevian Bible access error:',
        error
      );

      setStatus(
        error?.message ||
          'The controlled Bible could not be opened.',
        'error'
      );

    } finally {
      if (button) {
        button.disabled = false;
        button.textContent = originalText;
      }
    }
  }

  /* ============================================================
     STYLES
     ============================================================ */

  function installStyles() {
    if (
      document.getElementById(
        'cvn-executive-command-styles'
      )
    ) {
      return;
    }

    const style =
      document.createElement('style');

    style.id =
      'cvn-executive-command-styles';

    style.textContent = `
      #cvnExecutiveLauncher {
        position: fixed;
        right: 24px;
        bottom: 82px;
        z-index: 9001;
        border: 1px solid rgba(25,181,176,.58);
        background: linear-gradient(
          135deg,
          #0B1426,
          #10263B
        );
        color: #ffffff;
        padding: 12px 18px;
        border-radius: 9px;
        font-family: inherit;
        font-size: 12px;
        font-weight: 800;
        letter-spacing: .75px;
        cursor: pointer;
        box-shadow:
          0 12px 32px rgba(0,0,0,.30),
          0 0 18px rgba(25,181,176,.08);
        transition:
          transform .18s ease,
          border-color .18s ease,
          box-shadow .18s ease;
      }

      #cvnExecutiveLauncher:hover {
        transform: translateY(-1px);
        border-color: #19B5B0;
        box-shadow:
          0 14px 36px rgba(0,0,0,.34),
          0 0 22px rgba(25,181,176,.16);
      }

      #cvnExecutiveOverlay {
        position: fixed;
        inset: 0;
        z-index: 12500;
        display: none;
        background: rgba(4,12,25,.88);
        backdrop-filter: blur(6px);
        overflow-y: auto;
        padding: 30px;
        box-sizing: border-box;
      }

      #cvnExecutiveOverlay.cvn-executive-open {
        display: block;
      }

      .cvn-executive-shell {
        width: min(1180px,100%);
        margin: 0 auto;
        border-radius: 18px;
        overflow: hidden;
        background: #F7F9FC;
        box-shadow:
          0 32px 90px rgba(0,0,0,.48);
      }

      .cvn-executive-header {
        position: relative;
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 24px;
        padding: 34px 38px;
        background:
          radial-gradient(
            circle at 85% 10%,
            rgba(25,181,176,.15),
            transparent 30%
          ),
          linear-gradient(
            135deg,
            #081321,
            #10283D
          );
        color: #ffffff;
      }

      .cvn-executive-eyebrow {
        margin-bottom: 8px;
        color: #19B5B0;
        font-size: 10px;
        font-weight: 800;
        letter-spacing: 2px;
        text-transform: uppercase;
      }

      .cvn-executive-title {
        margin: 0;
        color: #ffffff !important;
        font-size: 29px;
        line-height: 1.15;
        font-weight: 800;
      }

      .cvn-executive-subtitle {
        max-width: 700px;
        margin-top: 9px;
        color: rgba(255,255,255,.66);
        font-size: 13px;
        line-height: 1.65;
      }

      .cvn-executive-close {
        flex: 0 0 auto;
        width: 40px;
        height: 40px;
        border: 1px solid rgba(255,255,255,.20);
        border-radius: 9px;
        background: rgba(255,255,255,.07);
        color: #ffffff;
        cursor: pointer;
        font-size: 21px;
      }

      .cvn-executive-content {
        padding: 34px 38px 42px;
      }

      .cvn-executive-identity {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 20px;
        margin-bottom: 24px;
        padding: 15px 18px;
        border: 1px solid #DDE4EC;
        border-radius: 11px;
        background: #ffffff;
      }

      .cvn-executive-identity-label {
        margin-bottom: 3px;
        color: #778494;
        font-size: 9px;
        font-weight: 800;
        letter-spacing: 1.2px;
        text-transform: uppercase;
      }

      .cvn-executive-identity-name {
        color: #0B1426;
        font-size: 14px;
        font-weight: 800;
      }

      .cvn-executive-security {
        padding: 6px 10px;
        border: 1px solid rgba(25,181,176,.25);
        border-radius: 999px;
        background: rgba(25,181,176,.08);
        color: #087B77;
        font-size: 9px;
        font-weight: 800;
        letter-spacing: .8px;
        text-transform: uppercase;
        white-space: nowrap;
      }

      .cvn-bible-card {
        position: relative;
        overflow: hidden;
        padding: 30px;
        border: 1px solid #DCE3EB;
        border-radius: 15px;
        background: #ffffff;
        box-shadow:
          0 10px 30px rgba(11,20,38,.06);
      }

      .cvn-bible-card::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 5px;
        height: 100%;
        background: #19B5B0;
      }

      .cvn-bible-kicker {
        margin-bottom: 9px;
        color: #087B77;
        font-size: 10px;
        font-weight: 800;
        letter-spacing: 1.7px;
        text-transform: uppercase;
      }

      .cvn-bible-title {
        max-width: 800px;
        margin: 0;
        color: #0B1426 !important;
        font-size: 24px;
        line-height: 1.25;
        font-weight: 800;
      }

      .cvn-bible-description {
        max-width: 820px;
        margin-top: 10px;
        color: #647386;
        font-size: 13px;
        line-height: 1.7;
      }

      .cvn-bible-meta {
        display: grid;
        grid-template-columns:
          repeat(4,minmax(0,1fr));
        gap: 12px;
        margin-top: 24px;
      }

      .cvn-bible-meta-item {
        padding: 14px;
        border: 1px solid #E2E7ED;
        border-radius: 9px;
        background: #F8FAFC;
      }

      .cvn-bible-meta-label {
        margin-bottom: 5px;
        color: #788596;
        font-size: 9px;
        font-weight: 800;
        letter-spacing: 1px;
        text-transform: uppercase;
      }

      .cvn-bible-meta-value {
        color: #152338;
        font-size: 12px;
        font-weight: 800;
        overflow-wrap: anywhere;
      }

      .cvn-bible-actions {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
        margin-top: 25px;
      }

      .cvn-bible-button {
        min-width: 155px;
        padding: 11px 17px;
        border-radius: 8px;
        font-family: inherit;
        font-size: 11px;
        font-weight: 800;
        letter-spacing: .4px;
        cursor: pointer;
      }

      .cvn-bible-button:disabled {
        opacity: .55;
        cursor: wait;
      }

      .cvn-bible-primary {
        border: 1px solid #0B1426;
        background: #0B1426;
        color: #ffffff;
      }

      .cvn-bible-primary:hover:not(:disabled) {
        background: #10283D;
      }

      .cvn-bible-secondary {
        border: 1px solid #CBD4DF;
        background: #ffffff;
        color: #0B1426;
      }

      .cvn-bible-secondary:hover:not(:disabled) {
        border-color: #19B5B0;
      }

      .cvn-executive-status {
        min-height: 18px;
        margin-top: 16px;
        color: #667486;
        font-size: 11px;
        line-height: 1.5;
      }

      .cvn-executive-status-success {
        color: #087B77;
      }

      .cvn-executive-status-error {
        color: #9C3838;
      }

      .cvn-executive-footer-note {
        margin-top: 22px;
        padding-top: 18px;
        border-top: 1px solid #E4E8ED;
        color: #7B8796;
        font-size: 10px;
        line-height: 1.6;
      }

      @media (max-width: 800px) {
        #cvnExecutiveOverlay {
          padding: 12px;
        }

        #cvnExecutiveLauncher {
          right: 14px;
          bottom: 76px;
        }

        .cvn-executive-header,
        .cvn-executive-content {
          padding-left: 20px;
          padding-right: 20px;
        }

        .cvn-executive-title {
          font-size: 24px;
        }

        .cvn-executive-identity {
          align-items: flex-start;
          flex-direction: column;
        }

        .cvn-bible-meta {
          grid-template-columns:
            repeat(2,minmax(0,1fr));
        }
      }

      @media (max-width: 480px) {
        .cvn-bible-meta {
          grid-template-columns: 1fr;
        }

        .cvn-bible-actions {
          flex-direction: column;
        }

        .cvn-bible-button {
          width: 100%;
        }
      }
    `;

    document.head.appendChild(style);
  }

  /* ============================================================
     INTERFACE
     ============================================================ */

  function installExecutiveUI(record) {
    if (executiveInstalled) return;

    executiveInstalled = true;
    bibleRecord = record;

    installStyles();

    const launcher =
      document.createElement('button');

    launcher.id = 'cvnExecutiveLauncher';
    launcher.type = 'button';
    launcher.textContent =
      'Executive Command Center';

    const overlay =
      document.createElement('div');

    overlay.id = 'cvnExecutiveOverlay';

    overlay.innerHTML = `
      <div class="cvn-executive-shell">

        <div class="cvn-executive-header">

          <div>
            <div class="cvn-executive-eyebrow">
              Corevian Executive Office
            </div>

            <h2 class="cvn-executive-title">
              Executive Command Center
            </h2>

            <div class="cvn-executive-subtitle">
              Private executive environment for Corevian's
              institutional operating system, controlled master
              resources and CEO governance materials.
            </div>
          </div>

          <button
            type="button"
            id="cvnExecutiveClose"
            class="cvn-executive-close"
            aria-label="Close Executive Command Center"
          >
            ×
          </button>

        </div>

        <div class="cvn-executive-content">

          <div class="cvn-executive-identity">

            <div>
              <div class="cvn-executive-identity-label">
                Authorized Executive
              </div>

              <div class="cvn-executive-identity-name">
                Raquia Compass —
                Founder &amp; Chief Executive Officer
              </div>
            </div>

            <div class="cvn-executive-security">
              CEO Restricted
            </div>

          </div>

          <div class="cvn-bible-card">

            <div class="cvn-bible-kicker">
              Enterprise Operating System
            </div>

            <h3 class="cvn-bible-title">
              ${escapeHTML(record.title)}
            </h3>

            <div class="cvn-bible-description">
              Corevian's controlled institutional knowledge master
              governing company foundation, executive governance,
              client acquisition, service activation, operational
              control, financial administration, workforce
              readiness, technology and controlled-document
              architecture.
            </div>

            <div class="cvn-bible-meta">

              <div class="cvn-bible-meta-item">
                <div class="cvn-bible-meta-label">
                  Document
                </div>
                <div class="cvn-bible-meta-value">
                  ${escapeHTML(record.document_code)}
                </div>
              </div>

              <div class="cvn-bible-meta-item">
                <div class="cvn-bible-meta-label">
                  Version
                </div>
                <div class="cvn-bible-meta-value">
                  ${escapeHTML(record.version)}
                </div>
              </div>

              <div class="cvn-bible-meta-item">
                <div class="cvn-bible-meta-label">
                  Effective Date
                </div>
                <div class="cvn-bible-meta-value">
                  ${formatDate(record.effective_date)}
                </div>
              </div>

              <div class="cvn-bible-meta-item">
                <div class="cvn-bible-meta-label">
                  Classification
                </div>
                <div class="cvn-bible-meta-value">
                  ${escapeHTML(record.classification)}
                </div>
              </div>

            </div>

            <div class="cvn-bible-actions">

              <button
                type="button"
                id="cvnBibleOpen"
                class="cvn-bible-button cvn-bible-primary"
              >
                Open Bible
              </button>

              <button
                type="button"
                id="cvnBibleDownload"
                class="cvn-bible-button cvn-bible-secondary"
              >
                Download Controlled Copy
              </button>
                <button
               type="button"
                 id="cvnBibleEnrollLauncher"
                 class="cvn-bible-button cvn-bible-secondary"
                 >
                  Set Up / Manage Authenticator
                 </button>
            </div>

            <div
              id="cvnExecutiveStatus"
              class="cvn-executive-status"
              aria-live="polite"
            >
              Secure CEO access verified.
            </div>

            <div class="cvn-executive-footer-note">
              CEO ONLY — CONFIDENTIAL. Access to this controlled
              resource is authorized through the authenticated
              Corevian University session and enforced by private
              Supabase database and storage policies.
            </div>

          </div>

        </div>
      </div>
    `;

     // document.body.appendChild(launcher);
    document.body.appendChild(overlay);

    launcher.addEventListener(
      'click',
      function () {
        overlay.classList.add(
          'cvn-executive-open'
        );

        document.body.style.overflow =
          'hidden';
      }
    );

    document
      .getElementById('cvnExecutiveClose')
      .addEventListener(
        'click',
        closeExecutiveCommandCenter
      );

    document
      .getElementById('cvnBibleOpen')
      .addEventListener(
        'click',
        () => openBible(false)
      );

    document
      .getElementById('cvnBibleDownload')
      .addEventListener(
        'click',
        () => openBible(true)
      );
      document
  .getElementById('cvnBibleEnrollLauncher')
  .addEventListener('click', () => {
    if (typeof window.openCorevianBibleEnrollment === 'function') {
      window.openCorevianBibleEnrollment();
    }
  });

    overlay.addEventListener(
      'click',
      function (event) {
        if (event.target === overlay) {
          closeExecutiveCommandCenter();
        }
      }
    );

    document.addEventListener(
      'keydown',
      function (event) {
        if (
          event.key === 'Escape' &&
          overlay.classList.contains(
            'cvn-executive-open'
          )
        ) {
          closeExecutiveCommandCenter();
        }
      }
    );
  }

  function closeExecutiveCommandCenter() {
    const overlay =
      document.getElementById(
        'cvnExecutiveOverlay'
      );

    if (overlay) {
      overlay.classList.remove(
        'cvn-executive-open'
      );
    }

    document.body.style.overflow = '';
  }

   /* ============================================================
   CEO ACCESS GATE
   ============================================================ */

async function initializeExecutiveCommandCenter() {
  /*
    Never expose CEO controls in Executive Demo.
  */
  if (isDemoMode()) {
    return;
  }

  try {
    /*
      STEP 1:
      Obtain the SAME authenticated Supabase session used by
      Corevian University's production authentication.
    */

    const session =
      await getAuthenticatedSession();

    if (!session?.user?.id) {
      return;
    }

    /*
      STEP 2:
      Exact-user authorization.

      Only Raquia Compass's authenticated account may continue.
      Marcell, future executives, administrators and all other
      users fail here before any Executive Command Center UI
      is installed.
    */

    if (!isAuthorizedCEO(session)) {
      console.info(
        'Corevian Executive Command Center: current authenticated user is not the authorized CEO.'
      );

      return;
    }

    /*
      STEP 3:
      Require the protected CEO Bible record through RLS.
    */

    const client =
      getUniversitySupabaseClient();

    if (!client) {
      return;
    }

    const record =
      await getBibleRecord(client);

    if (!record) {
      console.info(
        'Corevian Executive Command Center: CEO resource access was not confirmed by Supabase.'
      );

      return;
    }

    /*
      Only now may the interface exist.
    */

    installExecutiveUI(record);

  } catch (error) {
    console.info(
      'Corevian Executive Command Center: executive access not available.'
    );
  }
}


/* ============================================================
   START
   ============================================================ */

function start() {
  /*
    Corevian University restores its production session during
    startup. Give that authentication process time to complete,
    then read the session through the University's own client.
  */

  setTimeout(
    initializeExecutiveCommandCenter,
    1200
  );
}

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    start
  );
} else {
  start();
}

})();


  